import React, { useState, useEffect, useRef, useCallback } from 'react';
import useMedia from '../../hooks/useMedia';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getHomeworks } from '../../services/studentService';
import { isMistakeCleared, clearMistakes } from '../../utils/mistakesStore';
import { confirmDialog } from '../../components/Common/confirm';
import { bebasDigits } from '../../utils/bebasDigits';
import Collapse from '../../components/Common/Collapse';
import { slatLayout, slatVars, useRbCollapse, useRbJump } from '../../utils/rbSlats';
import TaskFiles from '../../components/Student/TaskFiles';

/* «Работа над ошибками» — тот же дизайн, что экран ДЗ («Домашка ученика»):
   стеклянная карточка, шкала-прогресс, сайдбар «Все номера», полоса ответа,
   результат с разбором. Отличие: результат НЕ сохраняется на сервер —
   «Проверить» даёт фидбек (номер не убирает), «Завершить попытку» убирает верно
   решённые из набора (в памяти сеанса). Попыток сколько угодно. */

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const GREEN = '#22c55e';
const ANSW = '#c9c1e0';

const normalize = (s) => (s ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
const checkAnswer = (hw, ua) => {
  if (!hw.correctAnswer) return false;
  if (hw.answerType === 'grid') {
    try { const correct = JSON.parse(hw.correctAnswer); const user = JSON.parse(ua || '[]'); if (!Array.isArray(correct) || !Array.isArray(user) || correct.length !== user.length) return false; return correct.every((v, i) => normalize(String(v)) === normalize(String(user[i] ?? ''))); }
    catch { return false; }
  }
  return normalize(ua) === normalize(hw.correctAnswer);
};

const IcBack = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>);
const IcArrow = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>);
const IcChevronL = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>);
const IcChevronD = ({ up }) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={up ? { transform: 'rotate(180deg)' } : undefined}><polyline points="6 9 12 15 18 9" /></svg>);
const IcUp = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>);

// Ответ в разборе: у сетки — значения ячеек через пробел, у остальных — как есть
const fmtAns = (h, v) => {
  const s = String(v ?? '').trim();
  if (!s) return '';
  if (h.answerType === 'grid') {
    try { const a = JSON.parse(s); if (Array.isArray(a)) return a.map(x => String(x ?? '').trim()).filter(Boolean).join(' '); } catch { /* не JSON — показываем как есть */ }
  }
  return s;
};

export default function MistakesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCourseId } = useOutletContext() || {};
  // Узкий экран: подпись «Ответ» слева от поля скрыта — подсказка в поле короче
  const narrowAns = useMedia('(max-width: 860px)');
  const [allWrong, setAllWrong] = useState([]);
  // Ширина полосы под плитки-номера: по ней считаем, сколько их влезает в ряд.
  const slatRO = useRef(null);
  const [slatBox, setSlatBox] = useState(0);
  const slatsRef = useCallback((el) => {
    slatRO.current?.disconnect();
    if (!el) return;
    setSlatBox(el.clientWidth);
    slatRO.current = new ResizeObserver(([e]) => setSlatBox(e.contentRect.width));
    slatRO.current.observe(el);
  }, []);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);      // текущая попытка (пронумерована seq)
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState({}); // hwId -> 'correct'|'incorrect'
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [started, setStarted] = useState(false); // превью перед разбором (как интро в ДЗ)
  const [jumpTo, setJumpTo] = useState(null);    // номер, к которому перешли по квадратику
  const jumpTimer = useRef(null);                // снятие подсветки: перезаводим на каждый клик
  // Свернуть/развернуть шапку разбора — остаются только плитки-номера
  const rb = useRbCollapse(setSlatBox);

  const buildList = useCallback((wrong) => wrong.filter(h => !isMistakeCleared(h.id)).map((h, i) => ({ ...h, seq: i + 1 })), []);
  const startAttempt = useCallback((wrong) => {
    setList(buildList(wrong)); setStep(0); setAnswers({}); setChecked({}); setFinished(false); setResults(null); setOpenRows({}); setOnlyErrors(false); setStarted(false);
  }, [buildList]);

  useEffect(() => {
    setLoading(true);
    getHomeworks(selectedCourseId).then(r => {
      /* Идёт повторная попытка — номера этого задания пропускаем: разбора по ним
         до её конца нет. Ручные номера тоже мимо: эталонного ответа у них нет,
         и «правильный ответ: —» ничему не научит — обратная связь куратора
         лежит в самом задании. */
      const wrong = (r.data || []).filter(h => h.lessonCompleted && !h.lessonRetryActive && !h.manualReview && h.answer?.status === 'incorrect').map(h => ({
        id: h.id, description: h.description, correctAnswer: h.correctAnswer, answerType: h.answerType, gridRows: h.gridRows, gridCols: h.gridCols,
        attachedFiles: h.attachedFiles || [], attachedSolutionFiles: h.attachedSolutionFiles || [], textSolution: h.textSolution, videoSolution: h.videoSolution,
        lessonId: h.lessonId ?? h.Lesson?.id, lessonTitle: h.Lesson?.title,
      }));
      setAllWrong(wrong); startAttempt(wrong);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedCourseId, startAttempt]);

  useRbJump(jumpTo);   // переход к номеру: см. utils/rbSlats

  const n = list.length;
  const cur = list[Math.min(step, Math.max(0, n - 1))];

  const setAns = (hwId, v) => { setAnswers(p => ({ ...p, [hwId]: v })); setChecked(c => ({ ...c, [hwId]: undefined })); };
  const gridValue = (id, rows, cols) => { try { const flat = JSON.parse(answers[id] || '[]'); return Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (_, c) => flat[r * cols + c] ?? '')); } catch { return Array.from({ length: rows }, () => Array(cols).fill('')); } };
  const setGridCell = (id, r, c, value, rows, cols) => { setChecked(ch => ({ ...ch, [id]: undefined })); setAnswers(prev => { let flat; try { flat = JSON.parse(prev[id] || '[]'); } catch { flat = []; } while (flat.length < rows * cols) flat.push(''); flat[r * cols + c] = value; return { ...prev, [id]: JSON.stringify(flat) }; }); };
  // Вставка нескольких ответов сразу: «12 34 56» из буфера раскладывается по ячейкам
  // начиная с той, куда вставляют (как в админке при создании номера).
  const pasteGrid = (e, id, r, c, rows, cols) => {
    const tokens = e.clipboardData.getData('text').split(/\s+/).filter(Boolean);
    if (tokens.length <= 1) return;
    e.preventDefault();
    setChecked(ch => ({ ...ch, [id]: undefined }));
    setAnswers(prev => {
      let flat; try { flat = JSON.parse(prev[id] || '[]'); } catch { flat = []; }
      while (flat.length < rows * cols) flat.push('');
      let idx = 0;
      for (let p = r * cols + c; p < rows * cols && idx < tokens.length; p++, idx++) flat[p] = tokens[idx];
      return { ...prev, [id]: JSON.stringify(flat) };
    });
  };

  const hasAnswer = (hw) => {
    const v = answers[hw.id]; if (v == null) return false;
    if (hw.answerType === 'grid') { const need = (hw.gridRows || 3) * (hw.gridCols || 2); try { const arr = JSON.parse(v); if (!Array.isArray(arr) || arr.length < need) return false; for (let i = 0; i < need; i++) if (String(arr[i] ?? '').trim() === '') return false; return true; } catch { return false; } }
    return String(v).trim() !== '';
  };
  const stateOf = (h) => checked[h.id] === 'correct' ? 'correct' : hasAnswer(h) ? 'answered' : 'empty';
  const correctCount = list.filter(h => checked[h.id] === 'correct').length;
  const answeredCount = list.filter(h => stateOf(h) === 'answered').length;
  const remainingCount = Math.max(0, n - correctCount - answeredCount);

  const check = () => {
    if (!cur) return;
    if (!hasAnswer(cur)) { toast.error('Введите ответ'); return; }
    const ok = checkAnswer(cur, answers[cur.id]);
    setChecked(c => ({ ...c, [cur.id]: ok ? 'correct' : 'incorrect' }));
    if (ok) toast.success('Верно!'); else toast.error('Пока неверно', { style: { background: '#ffe5e5', color: '#b91c1c' } });
  };

  const finish = async () => {
    const allFilled = list.every(hasAnswer);
    if (!allFilled) { const ok = await confirmDialog({ title: 'Завершить попытку?', message: 'Есть незаполненные номера — пойдут как неверные. Завершить?', confirmText: 'Завершить' }); if (!ok) return; }
    const res = list.map(h => ({ h, ok: checkAnswer(h, answers[h.id] || ''), user: answers[h.id] || '' }));
    clearMistakes(res.filter(r => r.ok).map(r => r.h.id));
    setResults(res); setFinished(true); setOpenRows({}); setOnlyErrors(false);
  };

  const goBack = () => navigate(location.state?.from || '/progress');
  const empty = n === 0 && !finished;

  if (loading) return <div className="lesson-full lesson-loading" style={{ background: 'var(--bg)' }}><div className="lesson-spinner" /></div>;

  // ─────────── РЕЗУЛЬТАТ ───────────
  if (finished) {
    const total = results.length;
    const correct = results.filter(r => r.ok).length;
    const pct = total ? Math.round((correct / total) * 100) : 0;
    const shown = onlyErrors ? results.filter(r => !r.ok) : results;
    const openNumber = (g) => {
      setOnlyErrors(false);
      setOpenRows(p => ({ ...p, [g.h.id]: true }));
      setJumpTo(g.h.id);
      clearTimeout(jumpTimer.current);
      jumpTimer.current = setTimeout(() => setJumpTo(null), 900);   // сам переход — в эффекте выше
    };
    return (
      <div className="lesson-full" style={{ background: 'var(--bg)' }}>
        <div className="nhw-wrap">
          <div className="nhw-card fixed" style={{ maxWidth: 'none' }}>
            {/* ── Баннер результата (дизайн 1b) ── */}
            <div className={`rb-hero${rb.min ? ' min' : ''}${rb.fade ? ' fade' : ''}`} ref={rb.heroRef}>
              {/* Свернуть шапку: останутся плитки-номера, чтобы переключаться по разбору */}
              <button type="button" className="rb-toggle" onClick={rb.toggle} aria-expanded={!rb.min}
                title={rb.min ? 'Развернуть' : 'Свернуть'} aria-label={rb.min ? 'Развернуть шапку' : 'Свернуть шапку'}>
                <IcUp />
              </button>
              <div className="rb-top">
                <button className="rb-back" onClick={goBack} title="Назад"><IcBack /></button>
                <span className="rb-crumb">Работа над ошибками</span>
              </div>

              <div className="rb-main">
                <div className="rb-score">
                  <div className="rb-pct">{pct}%</div>
                  <div className="rb-of"><b>{correct}</b> из <b>{total}</b> верно</div>
                </div>

                <div className="rb-right">
                  <div className="rb-slats" ref={slatsRef} style={slatVars(slatLayout(total, slatBox))}>
                    {results.map((g, i) => (
                      <button key={g.h.id} type="button"
                        className={`rb-slat${g.ok ? ' ok' : ''}${jumpTo === g.h.id ? ' hit' : ''}`}
                        title={`№${i + 1} — ${g.ok ? 'верно' : 'с ошибкой'}`}
                        onClick={() => openNumber(g)}>
                        <span className="rb-slat-n">№{i + 1}</span>
                        <span className="rb-slat-sq" />
                      </button>
                    ))}
                  </div>

                  <div className="rb-acts">
                    <button className="rb-fix" onClick={() => startAttempt(allWrong)}>Пройти ещё раз</button>
                    <button className="rb-next" onClick={goBack}>Готово</button>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 34px 30px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>Разбор ответов</div>
                <div style={{ flex: 1 }} />
                <button className="nhw-filt" onClick={() => setOnlyErrors(v => !v)} style={{ padding: '7px 14px', border: `1px solid ${onlyErrors ? 'var(--primary)' : 'var(--lg-edge)'}`, borderRadius: 30, background: onlyErrors ? 'var(--primary-light)' : 'var(--card)', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: onlyErrors ? 'var(--primary-dark)' : 'var(--text-2)', cursor: 'pointer' }}>Только с ошибками</button>
                <button className="nhw-filt" onClick={() => { const all = {}; shown.forEach(g => all[g.h.id] = true); setOpenRows(prev => shown.length && shown.every(g => prev[g.h.id]) ? {} : all); }} style={{ padding: '7px 14px', border: '1px solid var(--lg-edge)', borderRadius: 30, background: 'var(--card)', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer' }}>{shown.length && shown.every(g => openRows[g.h.id]) ? 'Свернуть все' : 'Развернуть все'}</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {shown.map((g) => {
                  const open = !!openRows[g.h.id]; const files = g.h.attachedSolutionFiles || [];
                  return (
                    <div key={g.h.id} id={`rb-row-${g.h.id}`} className={jumpTo === g.h.id ? 'rb-row hit' : 'rb-row'}
                      style={{ borderTop: '1px solid var(--lg-edge)', background: open ? 'var(--bg)' : 'transparent' }}>
                      <button className="nhw-acc rb-head" onClick={() => setOpenRows(p => ({ ...p, [g.h.id]: !p[g.h.id] }))} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '15px 4px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                        <span style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, background: g.ok ? 'rgba(34,197,94,0.13)' : 'var(--border)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {g.ok ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>}
                        </span>
                        <span style={{ fontFamily: MONO, fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)', width: 34, flexShrink: 0 }}>№{g.h.seq}</span>
                        <span style={{ flex: 1, fontSize: 13.5, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.h.lessonTitle ? `из ДЗ «${g.h.lessonTitle}»` : `Номер ${g.h.seq}`}</span>
                        {!g.ok && g.user && String(g.user).trim() && g.h.answerType !== 'grid' && <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 500, color: 'var(--text-3)', textDecoration: 'line-through' }}>{g.user}</span>}
                        {g.h.answerType !== 'grid' && <span style={{ fontFamily: MONO, fontSize: 14.5, fontWeight: 700, color: GREEN }}>{g.h.correctAnswer || '—'}</span>}
                        <span style={{ color: open ? 'var(--primary)' : 'var(--text-3)' }}><IcChevronD up={open} /></span>
                      </button>
                      <Collapse open={open}>
                        <div style={{ padding: '2px 4px 22px 74px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                          {g.h.description && <div className="hw-rich" style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-2)', maxWidth: 620 }} dangerouslySetInnerHTML={{ __html: g.h.description }} />}
                          <TaskFiles files={g.h.attachedFiles} style={{ marginTop: 0 }} />
                          {/* Верно — одно поле, ответ зелёным. Ошибка — рядом правильный ответ */}
                          <div className="rb-answers">
                            <div className="rb-yours">
                              <span className="rb-cap">Ваш ответ</span>
                              <span className={`rb-yours-v${g.ok ? ' ok' : ''}`}>{fmtAns(g.h, g.user) || 'нет ответа'}</span>
                            </div>
                            {!g.ok && (
                              <div className="rb-yours">
                                <span className="rb-cap">Правильный ответ</span>
                                <span className="rb-yours-v ok">{fmtAns(g.h, g.h.correctAnswer) || '—'}</span>
                              </div>
                            )}
                          </div>
                          {(g.h.textSolution || files.length > 0) && (
                            <div style={{ borderLeft: '2px solid var(--primary-light)', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-3)' }}>Решение</div>
                              {g.h.textSolution && <div className="hw-rich" style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--text-2)', maxWidth: 620 }} dangerouslySetInnerHTML={{ __html: g.h.textSolution }} />}
                              <TaskFiles files={files} style={{ marginTop: 0 }} />
                            </div>
                          )}
                        </div>
                      </Collapse>
                    </div>
                  );
                })}
                <div style={{ borderTop: '1px solid var(--lg-edge)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22, flexWrap: 'wrap' }}>
                <button onClick={() => startAttempt(allWrong)} style={{ height: 46, padding: '0 22px', border: 'none', borderRadius: 13, background: 'linear-gradient(150deg,#8b5cf6,#6d28d9)', color: '#fff', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Пройти ещё раз</button>
                <button className="nhw-ghost" onClick={goBack} style={{ height: 46, padding: '0 20px', border: '1px solid var(--lg-edge)', background: 'var(--card)', borderRadius: 13, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer' }}>В Прогресс</button>
              </div>
              {/* Распорка: чтобы последние номера тоже могли встать под верх экрана */}
              <div className="rb-tail" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────── ПУСТО ───────────
  if (empty) {
    return (
      <div className="lesson-full" style={{ background: 'var(--bg)' }}>
        <div className="nhw-wrap center">
          <div className="nhw-card" style={{ maxWidth: 480 }}>
            <div className="nhw-content" style={{ overflow: 'visible', textAlign: 'center', padding: '40px 30px' }}>
              <div style={{ width: 76, height: 76, borderRadius: 22, margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(150deg,#4ade80,#16a34a)', color: '#fff', boxShadow: '0 14px 34px -14px rgba(22,163,74,0.7)' }}>
                <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <h2 style={{ fontSize: 23, fontWeight: 800, color: 'var(--text-1)', margin: '0 0 8px' }}>Ошибок нет</h2>
              <p style={{ fontSize: 14, color: 'var(--text-2)', margin: '0 0 24px', lineHeight: 1.5 }}>В завершённых ДЗ не осталось номеров с ошибками. Отличная работа!</p>
              <button onClick={goBack} style={{ height: 46, padding: '0 24px', border: 'none', borderRadius: 13, background: 'linear-gradient(150deg,#8b5cf6,#6d28d9)', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Вернуться в Прогресс</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────── ПРЕВЬЮ (интро) ───────────
  if (!started) {
    const numWord = n === 1 ? 'номер' : n > 1 && n < 5 ? 'номера' : 'номеров';
    return (
      <div className="lesson-full" style={{ background: 'var(--bg)' }}>
        <div className="nhw-wrap center">
          <div className="nhw-card" style={{ maxWidth: 820 }}>
            <div className="nhw-content" style={{ overflow: 'visible', padding: '28px 48px 42px' }}>
              <button className="nhw-tb" onClick={goBack} title="Назад" style={{ width: 42, height: 42, borderRadius: 12, border: '1px solid var(--lg-edge)', background: 'var(--card)', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 22 }}><IcBack /></button>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 14 }}>Тренажёр</div>
              <div className="nhw-hero" style={{ fontSize: 46, marginBottom: 18 }}>{bebasDigits('Работа над ошибками')}</div>
              <div style={{ fontSize: 16, color: 'var(--text-2)', lineHeight: 1.55, maxWidth: 560 }}>
                {n} {numWord} с ошибками из всех завершённых ДЗ. Реши их заново — верно решённые уйдут из списка после завершения попытки.
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: 14, margin: '30px 0 14px' }}>
                {list.map((h) => <span key={h.id} style={{ flex: 1, height: 14, borderRadius: 5, background: 'var(--border)' }} />)}
              </div>
              <div style={{ display: 'flex', gap: 30, fontSize: 15, color: 'var(--text-2)' }}>
                <span>{n} {numWord}</span><span>Попытки без ограничений</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '22px 48px', borderTop: '1px solid var(--lg-edge)', background: 'var(--bg)' }}>
              <button onClick={() => setStarted(true)} style={{ height: 46, padding: '0 24px', border: 'none', borderRadius: 12, background: 'linear-gradient(150deg,#8b5cf6,#6d28d9)', color: '#fff', fontFamily: 'inherit', fontSize: 14.5, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 10px 22px -12px rgba(124,77,255,0.9)' }}>
                Начать разбор<IcArrow />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────── РЕШЕНИЕ НОМЕРА ───────────
  const isGrid = cur?.answerType === 'grid';
  const isLast = step === n - 1;
  return (
    <div className="lesson-full" style={{ background: 'var(--bg)' }}>
      <div className="nhw-wrap">
        <div className="nhw-card fixed">
          <div className="nhw-head">
            <button className="nhw-tb" onClick={goBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', border: '1px solid var(--lg-edge)', borderRadius: 10, background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}><IcBack /> Назад</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-1)' }}>Работа над ошибками</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>верно решённые уходят после завершения</div>
            </div>
          </div>

          <div className="nhw-scale">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 11, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>№{step + 1}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>из {n}</span>
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: 'var(--text-2)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: GREEN }} />верно {correctCount}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: ANSW }} />отвечено {answeredCount}</span>
                <span className="nhw-hide-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--border)' }} />осталось {remainingCount}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end', height: 30 }}>
              {list.map((h, i) => {
                const isCur = i === step;
                const bg = isCur ? 'var(--primary)' : stateOf(h) === 'correct' ? GREEN : stateOf(h) === 'answered' ? ANSW : 'var(--border)';
                return (
                  <button key={h.id} onClick={() => setStep(i)} title={`№${i + 1}`} style={{ flex: 1, height: isCur ? 30 : 10, minWidth: 0, border: 'none', borderRadius: isCur ? 6 : 4, background: bg, cursor: 'pointer', padding: 0, position: 'relative', boxShadow: isCur ? '0 6px 16px -6px rgba(124,77,255,0.8)' : 'none' }}>
                    {isCur && <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: '#fff' }}>{i + 1}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="nhw-body">
            <div className="nhw-content">
              <div className="nhw-hero" style={{ fontSize: 30, marginBottom: 8 }}>{bebasDigits(`Номер ${step + 1}`)}</div>
              {cur?.lessonTitle && <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--lg-edge)', color: 'var(--text-3)', borderRadius: 9, padding: '4px 10px', fontSize: 11.5, marginBottom: 14 }}>из ДЗ «{cur.lessonTitle}»</div>}
              {cur?.description && <div className="hw-rich" style={{ color: 'var(--text-2)', maxWidth: 640, whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: cur.description }} />}

              {/* Файлы к условию — такими же строками, как у админа в редакторе */}
              <TaskFiles files={cur?.attachedFiles} />

              {isGrid && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ overflowX: 'auto', marginBottom: 10 }}>
                    <table style={{ borderCollapse: 'collapse' }}><tbody>
                      {gridValue(cur.id, cur.gridRows || 3, cur.gridCols || 2).map((row, r) => (
                        <tr key={r}>{row.map((cell, c) => (
                          <td key={c} className="nhw-gcell"><input type="text" value={cell} onChange={e => setGridCell(cur.id, r, c, e.target.value, cur.gridRows || 3, cur.gridCols || 2)} onPaste={e => pasteGrid(e, cur.id, r, c, cur.gridRows || 3, cur.gridCols || 2)} /></td>
                        ))}</tr>
                      ))}
                    </tbody></table>
                  </div>
                  <button className="nhw-check" onClick={check} style={{ height: 40, padding: '0 20px', border: 'none', borderRadius: 10, background: 'var(--primary-light)', color: 'var(--primary-dark)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Проверить</button>
                </div>
              )}

              {checked[cur?.id] === 'incorrect' && <div style={{ marginTop: 12, fontSize: 13 }}><span className="hw-res-err">Пока неверно — попробуй ещё. Правильный ответ покажем после завершения.</span></div>}
            </div>

            <div className="nhw-side">
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-3)' }}>Все номера</div>
              <div className="nhw-numlist">
                {list.map((h, i) => {
                  const st = stateOf(h); const isCur = i === step;
                  const dot = isCur ? 'var(--primary)' : st === 'correct' ? GREEN : st === 'answered' ? ANSW : 'var(--border)';
                  return (
                    <button key={h.id} className={`nhw-numrow${isCur ? ' cur' : ''}`} onClick={() => setStep(i)}>
                      {/* значок-квадрат с номером — как у админа в панели номеров */}
                      <span className="nhw-numbadge">{i + 1}</span>
                      <span style={{ flex: 1, fontSize: 13.5, fontWeight: isCur ? 700 : 500, color: isCur ? 'var(--text-1)' : 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Номер {i + 1}</span>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="nhw-bottom">
            <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="nhw-hit" style={{ width: 42, height: 46, border: '1px solid var(--lg-edge)', background: 'var(--card)', borderRadius: 12, cursor: step === 0 ? 'default' : 'pointer', color: 'var(--text-2)', opacity: step === 0 ? 0.4 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IcChevronL /></button>
            {!isGrid && (
              <div className="nhw-ansbox" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, height: 46, padding: '0 6px 0 18px', border: '1.5px solid var(--lg-edge)', borderRadius: 13, background: 'var(--card)', minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 9 }}>
                  <span className="nhw-hide-sm" style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase', color: 'var(--text-3)', flexShrink: 0 }}>Ответ</span>
                  <input className="nhw-ans-input" value={answers[cur.id] || ''} onChange={e => setAns(cur.id, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') check(); }} placeholder={narrowAns ? 'Ответ' : 'введите значение'} style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'none', fontFamily: MONO, fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }} />
                </div>
                <button className="nhw-check" onClick={check} style={{ height: 34, padding: '0 18px', border: 'none', borderRadius: 9, background: 'var(--primary-light)', color: 'var(--primary-dark)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Проверить</button>
              </div>
            )}
            {isGrid && (
              <div className="nhw-ansbox grid" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, height: 46, padding: '0 18px', border: '1.5px solid var(--lg-edge)', borderRadius: 13, background: 'var(--bg)', minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 9 }}>
                  <span className="nhw-hide-sm" style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase', color: 'var(--text-3)', flexShrink: 0 }}>Ответ</span>
                  <span style={{ fontSize: 13.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>необходимо внести в сетку ответов</span>
                </div>
              </div>
            )}
            <button className="nhw-next" onClick={() => isLast ? finish() : setStep(s => Math.min(n - 1, s + 1))} style={{ height: 46, padding: '0 26px', border: 'none', borderRadius: 13, background: 'linear-gradient(150deg,#8b5cf6,#6d28d9)', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 9, boxShadow: '0 10px 24px -12px rgba(124,77,255,0.9)', flexShrink: 0 }}>
              {isLast ? 'Завершить' : 'Далее'}{!isLast && <IcArrow />}
            </button>
          </div>

          {checked[cur?.id] === 'correct' && (
            <div className="nhw-verdict" style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 22px', background: 'rgba(34,197,94,0.08)', borderTop: '1px solid rgba(34,197,94,0.2)', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: GREEN }}>Верно — уйдёт из ошибок после завершения</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
